import { useQuery, useMutation } from '@tanstack/react-query'

import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import type { IAppUser, IAppUserRequest, IResendAppsUserVerificationMailPayload, USER_TYPE } from '~/types/app-user'
import type { IAccessTokenResponse } from '~/types/authentication'
import type { DeviceType } from '~/types/device-type'

import {
  getAppUserDetails,
  searchUsers,
  resendAppsUserVerificationMail,
  updateAppsUserMail,
  updateAppsUserPassword,
  verifyAppsUserMail,
  getAccessToken,
  deleteAppUserAccount,
} from '~/api/app-users'

////////// KEYS //////////
export const keys = {
  all: ['app-users'] as const,
  details: (env: string, userId?: string) => [...keys.all, 'details', env, userId] as const,
  search: (id: string) => [...keys.all, 'search', id] as const,
}

////////// QUERIES //////////
export const useAppUserDetailsQuery = (env: string, userId?: string): UseQueryResult<IAppUser> =>
  useQuery({
    enabled: !!userId,
    queryFn: () => getAppUserDetails(env, userId),
    queryKey: keys.details(env, userId),
  })

export const useSearchUsersQuery = (id: string): UseQueryResult<IAppUserRequest> =>
  useQuery({
    enabled: !!id,
    queryFn: () => searchUsers(id),
    queryKey: keys.search(id),
  })

////////// MUTATIONS //////////
export const useResendAppsUserVerificationMailMutation = (): UseMutationResult<
  void,
  unknown,
  IResendAppsUserVerificationMailPayload,
  unknown
> => useMutation({ mutationFn: resendAppsUserVerificationMail })

export const useUpdateAppsUserMailMutation = (): UseMutationResult<
  void,
  unknown,
  { clientId: string; currentEmail: string; env: USER_TYPE; newEmail: string; userId: string },
  unknown
> => useMutation({ mutationFn: updateAppsUserMail })

export const useUpdateAppsUserPasswordMutation = (): UseMutationResult<
  void,
  unknown,
  { env: USER_TYPE; password: string; user_id: string },
  unknown
> => useMutation({ mutationFn: updateAppsUserPassword })

export const useVerifyAppsUserMailMutation = (): UseMutationResult<
  void,
  unknown,
  { clientId: string; email: string; env: USER_TYPE; userId: string },
  unknown
> => useMutation({ mutationFn: verifyAppsUserMail })

export const useGetAccessTokenMutation = (): UseMutationResult<
  IAccessTokenResponse,
  unknown,
  {
    appKey: string
    deviceLanguage: string
    deviceType: DeviceType
    env: USER_TYPE
    installationId: string
    userId: string
  },
  unknown
> => useMutation({ mutationFn: getAccessToken })

export const useDeleteAppUserAccountMutation = (): UseMutationResult<
  void,
  unknown,
  { env: USER_TYPE; userId: string },
  unknown
> => useMutation({ mutationFn: deleteAppUserAccount })
